"use client";

import { useCallback, useEffect, useState } from "react";
import { IconMapPin, IconX } from "@tabler/icons-react";
import { Button } from "@/components/bnp-ui";
import { useLocation } from "@/contexts/LocationContext";

const REVEAL_DELAY_MS = 350;
const EXIT_MS = 400;

const LocationRequest = () => {
  const {
    showLocationPrompt,
    handleRequestLocation,
    dismissLocationPrompt,
    isRequestingLocation,
    locationError,
  } = useLocation();
  const [revealed, setRevealed] = useState(false);

  // Slide/fade in shortly after mount
  useEffect(() => {
    const timer = window.setTimeout(() => setRevealed(true), REVEAL_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, []);

  const handleDismiss = useCallback(() => {
    // Animate out first, then hide via context (in-memory only, so a page
    // refresh re-shows the banner if permission still isn't granted).
    setRevealed(false);
    window.setTimeout(dismissLocationPrompt, EXIT_MS);
  }, [dismissLocationPrompt]);

  if (!showLocationPrompt) return null;

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-5 z-40 flex justify-center px-4">
      <div
        role="dialog"
        aria-live="polite"
        aria-label="Location access request"
        className={`pointer-events-auto relative w-full max-w-[1440px] rounded-2xl border border-[#C3D6DA] bg-[#DCE7E9] p-4 shadow-xl transition-all duration-500 ease-out ${
          revealed ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
        }`}
      >
        {/* Close button */}
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss location request"
          className="absolute top-2.5 right-2.5 rounded-full p-1 text-slate-500 transition-colors hover:bg-[#C3D6DA] hover:text-slate-700 cursor-pointer"
        >
          <IconX className="h-4 w-4" />
        </button>

        <div className="flex flex-col items-center gap-3 px-6 text-center">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#C3D6DA]">
            <IconMapPin className="h-5 w-5 text-[#19545E]" />
          </div>

          {/* Label / Prompt Text */}
          <p className="flex-1 text-sm font-medium text-[#0F172A]">
            Enable location access to see properties near you
          </p>

          {/* Action Button */}
          <Button
            className="bg-[#19545E] hover:bg-[#133E46] active:bg-[#133E46] text-white text-xs font-semibold h-[45px] w-full rounded-lg shadow-sm transition-colors duration-200 cursor-pointer"
            onClick={handleRequestLocation}
            isLoading={isRequestingLocation}
          >
            {isRequestingLocation
              ? "Getting your location..."
              : "Use my location"}
          </Button>

          {locationError && (
            <p role="alert" className="text-xs font-medium text-red-600">
              {locationError}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default LocationRequest;
